import { useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  BackgroundVariant,
  Panel,
  type NodeTypes,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import { useOntologyGraph } from '@/hooks/useOntologyGraph';
import { userAppContext } from '@/context/Context';
import type { UserAppContextType } from '@/router/types';
import { getExplorerRoots } from '@/services/ontologyGraph.service';
import { buildDiseaseGeneTree } from '@/utils/ontologyTree/buildDiseaseGeneTree';
import { useTreeLayout } from './useTreeLayout';
import { DiseaseNode } from './nodes/DiseaseNode';
import { GeneNode } from './nodes/GeneNode';
import { ActionNode } from './nodes/ActionNode';
import { GeneSelectionModal } from '@/components/OntologyGraph/GeneSelectionModal';
import { OntologyMoreList } from '@/components/OntologyGraph/OntologyMoreList';
import type { OntologyFlowNode } from '@/utils/ontologyTree/types';
import type { Edge } from '@xyflow/react';

const NODE_TYPES: NodeTypes = {
  disease: DiseaseNode as NodeTypes[string],
  gene: GeneNode as NodeTypes[string],
  action: ActionNode as NodeTypes[string],
};

type DiseaseGeneTreeProps = {
  rootNodeId?: string;
  rootNodeType?: 'id' | 'name';
};

export function DiseaseGeneTree({ rootNodeId, rootNodeType = 'id' }: DiseaseGeneTreeProps) {
  const navigate = useNavigate();
  const { restrictSources } = userAppContext() as unknown as UserAppContextType;

  const {
    nodes: ontologyNodes,
    edges: ontologyEdges,
    initialize,
    initializeExplorer,
    loading,
    error,
    onExpandNode,
    collapseNode,
    moreListParentId,
    moreListChildren,
    setMoreListParentId,
    openMoreList,
    onSelectMoreChildren,
    showMoreGenesDiseaseId,
    setShowMoreGenesDiseaseId,
    onShowMoreGenesChoice,
    geneSelectionDiseaseId,
    setGeneSelectionDiseaseId,
    onAddSelectedGenes,
    allGenesForDisease,
    onGeneClick,
    onShowRelatedDiseasesClick,
  } = useOntologyGraph({ rootNodeId, rootNodeType, moreThreshold: 10 });

  useEffect(() => {
    if (rootNodeId) {
      initialize();
    } else {
      let cancelled = false;
      getExplorerRoots(10).then(roots => {
        if (!cancelled) initializeExplorer(roots);
      });
      return () => {
        cancelled = true;
      };
    }
  }, [rootNodeId, initialize, initializeExplorer]);

  const handleNavigate = useCallback(
    (nodeId: string) => {
      const source = restrictSources === 'CURATED' ? 'CURATED' : 'ALL';
      navigate({
        pathname: '/search',
        search: `?view=DISEASES&idents=${encodeURIComponent(nodeId)}&source=${source}&tab=GDA`,
      });
    },
    [navigate, restrictSources],
  );

  const handleToggle = useCallback(
    (nodeId: string) => {
      onExpandNode(nodeId);
    },
    [onExpandNode],
  );

  const handleActionClick = useCallback(
    (nodeId: string, actionType: string, meta: Record<string, unknown>) => {
      if (actionType === 'show_more_genes') {
        const diseaseId = (meta.diseaseId as string) ?? nodeId.replace(/^__show_more_genes_/, '');
        setShowMoreGenesDiseaseId(diseaseId);
      }
      if (actionType === 'other_genes') {
        const diseaseId = (meta.diseaseId as string) ?? nodeId.replace(/^__other_genes_/, '');
        onExpandNode(nodeId);
        void diseaseId;
      }
    },
    [setShowMoreGenesDiseaseId, onExpandNode],
  );

  const { rawFlowNodes, rawFlowEdges } = useMemo(() => {
    const { flowNodes, flowEdges } = buildDiseaseGeneTree({
      nodes: ontologyNodes,
      edges: ontologyEdges,
      allGenesForDisease,
      onToggle: handleToggle,
      onCollapse: collapseNode,
      onNavigate: handleNavigate,
      onGeneClick,
      onShowRelatedDiseasesClick,
      onActionClick: handleActionClick,
      openMoreList,
    });

    return { rawFlowNodes: flowNodes, rawFlowEdges: flowEdges };
  }, [
    ontologyNodes,
    ontologyEdges,
    allGenesForDisease,
    handleToggle,
    collapseNode,
    handleNavigate,
    onGeneClick,
    onShowRelatedDiseasesClick,
    handleActionClick,
    openMoreList,
  ]);

  const layoutedNodes = useTreeLayout(rawFlowNodes, rawFlowEdges);

  const [nodes, setNodes, onNodesChange] = useNodesState<OntologyFlowNode>(layoutedNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>(rawFlowEdges);

  useEffect(() => {
    setNodes(layoutedNodes);
  }, [layoutedNodes, setNodes]);

  useEffect(() => {
    setEdges(rawFlowEdges);
  }, [rawFlowEdges, setEdges]);

  return (
    <div style={{ width: '100%', height: '100%', position: 'relative' }}>
      {loading && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(255,255,255,0.85)',
            zIndex: 10,
            fontSize: 14,
            color: '#555',
            gap: 10,
          }}
        >
          <span
            style={{
              width: 20,
              height: 20,
              border: '2px solid #4a90d9',
              borderTopColor: 'transparent',
              borderRadius: '50%',
              animation: 'spin 0.7s linear infinite',
              display: 'inline-block',
            }}
          />
          Loading tree…
        </div>
      )}
      {error && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#c00',
            fontSize: 14,
          }}
        >
          {error}
        </div>
      )}

      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        nodeTypes={NODE_TYPES}
        fitView
        fitViewOptions={{ padding: 0.2 }}
        minZoom={0.1}
        maxZoom={2.5}
        nodesDraggable={true}
        nodesConnectable={false}
        elementsSelectable={true}
        proOptions={{ hideAttribution: true }}
        style={{ background: '#f8fafc' }}
      >
        <Background variant={BackgroundVariant.Dots} gap={20} size={1} color="#dde4ed" />
        <Controls style={{ background: 'transparent', border: 'none', boxShadow: 'none', gap: 4 }} />
        <MiniMap
          nodeColor={n => {
            if (n.type === 'gene') return '#2e7d32';
            if (n.type === 'action') return '#9c27b0';
            return '#4a90d9';
          }}
          style={{ bottom: 16, right: 16, width: 130, height: 82 }}
          zoomable
          pannable
        />
        <Panel position="top-left" style={{ width: 'fit-content' }}>
          <div
            style={{
              background: 'rgba(255,255,255,0.9)',
              border: '1px solid #e0e8f0',
              borderRadius: 6,
              padding: '4px 10px',
              fontSize: 11,
              color: '#555',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 12,
              whiteSpace: 'nowrap',
            }}
          >
            <LegendItem color="#4a90d9" label="Disease" />
            <LegendItem color="#2e7d32" label="Gene" />
            <LegendItem color="#9c27b0" label="More genes" />
          </div>
        </Panel>
      </ReactFlow>

      {showMoreGenesDiseaseId && (
        <ShowMoreGenesInline
          diseaseId={showMoreGenesDiseaseId}
          onChoice={(diseaseId, choice) => {
            setShowMoreGenesDiseaseId(null);
            onShowMoreGenesChoice(diseaseId, choice);
          }}
          onClose={() => setShowMoreGenesDiseaseId(null)}
        />
      )}

      {geneSelectionDiseaseId && (
        <GeneSelectionModal
          diseaseId={geneSelectionDiseaseId}
          genes={allGenesForDisease(geneSelectionDiseaseId) ?? []}
          onAdd={onAddSelectedGenes}
          onClose={() => setGeneSelectionDiseaseId(null)}
        />
      )}

      {moreListParentId && (
        <OntologyMoreList
          parentId={moreListParentId}
          items={moreListChildren}
          onSelect={onSelectMoreChildren}
          onClose={() => setMoreListParentId(null)}
        />
      )}

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        .react-flow__controls {
          bottom: 56px !important;
        }
        .react-flow__controls-button {
          border-radius: 50% !important;
          width: 28px !important;
          height: 28px !important;
          box-shadow: 0 1px 4px rgba(0,0,0,0.14) !important;
          border: 1px solid #e0e8f0 !important;
          background: #fff !important;
          margin: 0 !important;
          padding: 0 !important;
        }
        .react-flow__controls-button:hover {
          background: #f0f7ff !important;
        }
        .react-flow__controls-button svg {
          max-width: 12px;
          max-height: 12px;
        }
      `}</style>
    </div>
  );
}

function LegendItem({ color, label }: { color: string; label: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <span
        style={{
          width: 10,
          height: 10,
          borderRadius: '50%',
          background: color,
          flexShrink: 0,
        }}
      />
      <span>{label}</span>
    </div>
  );
}

function ShowMoreGenesInline({
  diseaseId,
  onChoice,
  onClose,
}: {
  diseaseId: string;
  onChoice: (diseaseId: string, choice: 'next20' | 'manual' | 'all') => void;
  onClose: () => void;
}) {
  return (
    <>
      <div style={{ position: 'fixed', inset: 0, zIndex: 20 }} onClick={onClose} aria-hidden="true" />
      <div
        role="dialog"
        aria-label="Load more genes"
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 21,
          background: '#fff',
          border: '1px solid #e0e8f0',
          borderRadius: 12,
          boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
          padding: 24,
          minWidth: 280,
        }}
      >
        <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 600, color: '#1a2e44' }}>
          Load more genes
        </h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {(
            [
              { choice: 'next20' as const, label: 'Show next 20' },
              { choice: 'manual' as const, label: 'Select genes manually…' },
              { choice: 'all' as const, label: 'Show all genes' },
            ] as const
          ).map(({ choice, label }) => (
            <button
              key={choice}
              type="button"
              onClick={() => onChoice(diseaseId, choice)}
              style={{
                padding: '10px 16px',
                border: '1px solid #c8d6e5',
                borderRadius: 8,
                background: '#f0f7ff',
                color: '#2c6fad',
                fontSize: 13,
                fontWeight: 500,
                cursor: 'pointer',
                textAlign: 'left',
                transition: 'background 0.12s',
              }}
              onMouseOver={e => (e.currentTarget.style.background = '#dbeeff')}
              onMouseOut={e => (e.currentTarget.style.background = '#f0f7ff')}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
    </>
  );
}
